import type { VerifiedPaymentStatus } from './paymentState';

export interface GoogleAdsConfig {
  tagId: string;
  purchaseLabel: string;
  hosts: readonly string[];
}

export interface GoogleAdsEnvironment {
  managedConsent?: boolean;
  location: () => string;
  referrer: () => string;
  now: () => number;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  command: (...args: unknown[]) => void;
  loadTag: (tagId: string) => Promise<boolean>;
  clearAttribution: () => void;
}

export const ADS_PURCHASE_STORAGE = 'bukietowa.ads-purchases.v1';
const RETENTION_MS = 90 * 24 * 60 * 60 * 1000;
const AD_CLICK_KEYS = ['gclid', 'gbraid', 'wbraid'] as const;

/** Excludes payment secrets, searches, contact details and arbitrary URL input. */
export function adsPageLocation(href: string): string {
  try {
    const url = new URL(href);
    const clean = new URL(url.origin + url.pathname);
    for (const key of AD_CLICK_KEYS) {
      const value = url.searchParams.get(key);
      if (value && /^[A-Za-z0-9_-]{1,512}$/.test(value)) clean.searchParams.set(key, value);
    }
    return clean.href;
  } catch { return ''; }
}

function referrerOrigin(href: string): string {
  try { return new URL(href).origin + '/'; } catch { return ''; }
}

export function purchaseConversion(paymentIntentId: string | null, status: VerifiedPaymentStatus | null) {
  if (!paymentIntentId || !/^pi_[A-Za-z0-9]+$/.test(paymentIntentId)
    || status?.status !== 'succeeded' || status.orderStatus !== 'received'
    || status.currency !== 'pln' || !Number.isSafeInteger(status.amountGrosze) || status.amountGrosze <= 0) return null;
  return { transaction_id: paymentIntentId, value: status.amountGrosze / 100, currency: 'PLN' } as const;
}

const DENIED = {
  ad_storage: 'denied', analytics_storage: 'denied',
  ad_user_data: 'denied', ad_personalization: 'denied',
} as const;

/** Basic consent mode: the Google library is never requested before consent. */
export function createGoogleAds(config: GoogleAdsConfig, env: GoogleAdsEnvironment) {
  let allowed = false;
  let initialized = false;
  let ready = false;
  let loading: Promise<boolean> | null = null;
  const pending = new Set<string>();
  const sent = new Set<string>();
  const configured = /^AW-\d+$/.test(config.tagId) && /^[A-Za-z0-9_-]+$/.test(config.purchaseLabel);
  const validHost = () => {
    try { return config.hosts.includes(new URL(env.location()).hostname); } catch { return false; }
  };
  const permitted = () => allowed && configured && validHost();
  function savedPurchases(): Record<string, number> {
    try {
      const parsed: unknown = JSON.parse(env.storage.getItem(ADS_PURCHASE_STORAGE) ?? '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
      return Object.fromEntries(Object.entries(parsed).filter(([key, time]) => key.startsWith(config.tagId + '/')
        && typeof time === 'number' && time <= env.now() && time > env.now() - RETENTION_MS));
    } catch { return {}; }
  }
  const pageFields = () => ({ page_location: adsPageLocation(env.location()), page_referrer: referrerOrigin(env.referrer()) });
  async function updateConsent(marketing: boolean): Promise<boolean> {
    allowed = marketing;
    if (!configured || !validHost()) return false;
    if (!allowed) {
      if (initialized && !env.managedConsent) env.command('consent', 'update', DENIED);
      // Keep queued/completed IDs only in this page's memory across withdrawal;
      // persistent measurement state and attribution are still removed.
      try { env.storage.removeItem(ADS_PURCHASE_STORAGE); } catch { /* Storage can be blocked. */ }
      try { env.clearAttribution(); } catch { /* Cookie access can be blocked. */ }
      return false;
    }
    if (!initialized) {
      initialized = true;
      try {
        if (env.storage.getItem(ADS_PURCHASE_STORAGE)) env.storage.setItem(ADS_PURCHASE_STORAGE, JSON.stringify(savedPurchases()));
      } catch { /* Optional storage can be blocked. */ }
      if (!env.managedConsent) env.command('consent', 'default', DENIED);
      env.command('set', 'ads_data_redaction', true);
      env.command('set', 'allow_ad_personalization_signals', false);
      env.command('set', 'allow_enhanced_conversions', false);
      if (!env.managedConsent) {
        env.command('set', pageFields());
        env.command('js', new Date(env.now()));
      }
    }
    if (!env.managedConsent) env.command('consent', 'update', { ...DENIED, ad_storage: 'granted', ad_user_data: 'granted' });
    if (!ready && !loading) {
      // The Google tag captures consented ad clicks in its first-party cookies.
      env.command('config', config.tagId, {
        ...pageFields(), send_page_view: false, allow_enhanced_conversions: false,
        allow_ad_personalization_signals: false, cookie_expires: 90 * 24 * 60 * 60,
      });
      loading = env.loadTag(config.tagId).then(loaded => { ready = loaded; return loaded; }).catch(() => false).finally(() => { loading = null; });
    }
    if (loading) await loading;
    return ready && permitted();
  }
  function trackPurchase(paymentIntentId: string | null, status: VerifiedPaymentStatus | null): boolean {
    const purchase = purchaseConversion(paymentIntentId, status);
    if (!purchase || !ready || !permitted()) return false;
    const key = `${config.tagId}/${config.purchaseLabel}:${purchase.transaction_id}`;
    if (pending.has(key) || sent.has(key)) return false;
    if (savedPurchases()[key]) { sent.add(key); return false; }
    pending.add(key);
    env.command('event', 'conversion', {
      send_to: `${config.tagId}/${config.purchaseLabel}`, ...purchase, ...pageFields(),
      // Only retain a local duplicate marker after the library processes the
      // event. Google's transaction_id also deduplicates retries across tabs.
      event_callback: () => {
        pending.delete(key);
        sent.add(key);
        if (!permitted()) return;
        try {
          const saved = savedPurchases();
          saved[key] = env.now();
          env.storage.setItem(ADS_PURCHASE_STORAGE, JSON.stringify(saved));
        } catch { /* The in-memory guard and Google transaction ID still apply. */ }
      },
    });
    return true;
  }
  return { updateConsent, trackPurchase };
}
